import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { createContact } from "@/lib/api";
import { COMPANY } from "@/data";
import { Reveal, Overline } from "@/components/Reveal";

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-primary outline-none py-3 text-sm text-white placeholder:text-zinc-500 transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await createContact(form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 border-t border-white/10 bg-background py-24 md:py-32">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <Reveal><Overline>Get in Touch</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-white md:text-6xl">
              Your transportation freedom awaits.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-sm font-light leading-relaxed text-zinc-400">
              Send us a note about your upcoming trip, corporate account or special event.
              Our team responds promptly, 24 hours a day.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-10 text-xs uppercase tracking-[0.2em] text-zinc-600">{COMPANY.address}</p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
            <input data-testid="contact-name" className={field} placeholder="Name" value={form.name} onChange={set("name")} />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <input data-testid="contact-email" type="email" className={field} placeholder="Email" value={form.email} onChange={set("email")} />
              <input data-testid="contact-phone" className={field} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
            </div>
            <textarea data-testid="contact-message" rows={4} className={`${field} resize-none`} placeholder="How can we help?" value={form.message} onChange={set("message")} />
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group flex items-center gap-2 bg-primary px-9 py-4 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:tracking-[0.3em] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send Message <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
